/*
 * This file contains a centralized initialization of the environment variables.
 * Always use the exported environment object to access environment variables.
 */

export type EnvironmentIndicatorTone =
  | 'demo'
  | 'staging'
  | 'local'
  | 'development';

export type EnvironmentIndicator = {
  label: string;
  tone: EnvironmentIndicatorTone;
};

const formatEnvironmentName = (environment: string) =>
  environment
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());

export const getEnvironmentIndicator = (
  environment: string,
): EnvironmentIndicator | null => {
  const normalizedEnvironment = environment.trim().toLowerCase();

  if (normalizedEnvironment === 'production') {
    return null;
  }

  if (normalizedEnvironment.includes('demo')) {
    return { label: 'Demo', tone: 'demo' };
  }

  if (normalizedEnvironment.includes('staging')) {
    return { label: 'Staging', tone: 'staging' };
  }

  if (normalizedEnvironment === 'local') {
    return { label: 'Local', tone: 'local' };
  }

  return {
    label: formatEnvironmentName(normalizedEnvironment),
    tone: 'development',
  };
};

type Environment = {
  env: string;
  bffBaseUrl: string;
  sherloqMfeUrl: string;
  analyticsMfeUrl: string;
  messagingMfeUrl: string;
  patientEngagementMfeUrl: string;
  externalMonitorsMfeUrl: string;
  helpCenterUrl: string;
  vitalsMfeUrl: string;
  auth: {
    url: string;
    realm: string;
    clientId: string;
  };
  openReplay: {
    projectKey: string;
    ingestPoint: string;
  };
  // When true, the frontend behaves in legacy-compatible mode: filter selections
  // are serialized as option.value strings (e.g. 'high', 'InClinic') instead of
  // option.id UUIDs so the BFF legacy-data overrides can map them natively
  // without round-tripping through dynamic-ui. Use for any future legacy-only
  // behavior toggles as well.
  legacyMode: boolean;
  isLocal: () => boolean;
  isDevelopment: () => boolean;
  isProduction: () => boolean;
  isStaging: () => boolean;
  isDemo: () => boolean;
  /** Any recognised environment other than production; false when unset. */
  isNonProduction: () => boolean;
};

const env: Environment = {
  env: import.meta.env.VITE_ENVIRONMENT,
  bffBaseUrl: import.meta.env.VITE_BFF_BASE_URL,
  sherloqMfeUrl: import.meta.env.VITE_SHERLOQ_MFE_URL,
  messagingMfeUrl:
    import.meta.env.VITE_MESSAGING_MFE_URL ||
    'http://localhost:4237/src/mfe-parcel.tsx',
  analyticsMfeUrl:
    import.meta.env.VITE_ANALYTICS_MFE_URL ||
    'http://localhost:4239/src/mfe-parcel.tsx',
  patientEngagementMfeUrl:
    import.meta.env.VITE_PATIENT_ENGAGEMENT_MFE_URL ||
    'http://localhost:4238/src/mfe-parcel.tsx',
  externalMonitorsMfeUrl:
    import.meta.env.VITE_EXTERNAL_MONITORS_MFE_URL ||
    'http://localhost:4249/src/mfe-parcel.tsx',
  vitalsMfeUrl:
    import.meta.env.VITE_VITALS_MFE_URL ||
    'http://localhost:4250/src/mfe-parcel.tsx',
  helpCenterUrl: import.meta.env.VITE_HELP_CENTER_URL ?? '',
  auth: {
    url: import.meta.env.VITE_KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  },
  openReplay: {
    projectKey: import.meta.env.VITE_OPENREPLAY_PROJECT_KEY ?? '',
    ingestPoint: import.meta.env.VITE_OPENREPLAY_INGEST_POINT ?? '',
  },
  legacyMode: import.meta.env['VITE_LEGACY_MODE'] === 'true',
  isLocal: function (): boolean {
    return this.env === 'local';
  },
  // The shared dev cluster (`.env.development`, the development gitops configmap).
  isDevelopment: function (): boolean {
    return this.env === 'dev';
  },
  isProduction: function (): boolean {
    return this.env === 'production';
  },
  // Matched positively against 'staging' rather than derived from
  // `!isProduction()`: dev, local and the customer-facing demo environment must
  // NOT be labelled staging, and an unset/typo'd value must fail closed.
  isStaging: function (): boolean {
    return this.env === 'staging';
  },
  // The customer-facing demo environments are region-suffixed (`demo-us`, …),
  // so match the family the same way `getEnvironmentIndicator` does. An unset
  // value fails closed so no demo-only behaviour can leak into another env.
  isDemo: function (): boolean {
    return (this.env ?? '').trim().toLowerCase().includes('demo');
  },
  // For pre-release features that may show everywhere but production. A
  // positive match over the known environments rather than `!isProduction()`,
  // so an unset or typo'd value fails closed instead of enabling everything.
  isNonProduction: function (): boolean {
    return (
      this.isLocal() ||
      this.isDevelopment() ||
      this.isStaging() ||
      this.isDemo()
    );
  },
};

export default env;
